// Debug "View agent steps": renders one chat turn's full execution trace (conversation_runs.steps[]).
// Dark-themed to sit inline under an assistant bubble. `TurnSteps` is the container (toggle + lazy
// fetch by turn id); `AgentStepsView` is the pure timeline. Modeled on the platform SessionDebugger
// but for the step_type shape (user_input → llm_call → tool_call/tool_output → action → response).
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetConversationRunsQuery, type AgentRun, type AgentRunStep } from '@/redux/internal-services/agentApi';

type StepType = AgentRunStep['step_type'];

const NEUTRAL_BADGE = 'bg-ui-subtle text-ui-text2 border-ui-border';
const GOOD_BADGE = 'bg-success/15 text-success-text border-success/30';
const WARN_BADGE = 'bg-amber-500/15 text-amber-300 border-amber-500/30';
const BAD_BADGE = 'bg-danger/15 text-danger border-danger/30';

/** `label` is a fixed debug label; `labelKey` (card-trace steps) goes through i18n. */
const STEP_META: Record<StepType, { label?: string; labelKey?: string; badge: string }> = {
  user_input:     { labelKey: 'chat.agentSteps.labels.user', badge: GOOD_BADGE },
  llm_call:       { labelKey: 'chat.agentSteps.labels.llmCall', badge: 'bg-accent/15 text-accent-text border-accent/30' },
  // A live realtime session.update: the new prompt + tool list the model holds from here on.
  session_update: { label: 'Session update', badge: 'bg-steel/15 text-steel-text border-steel/30' },
  tool_call:      { labelKey: 'chat.agentSteps.labels.toolCall', badge: 'bg-warning/15 text-warning-text border-warning/30' },
  tool_output:    { labelKey: 'chat.agentSteps.labels.toolResult', badge: 'bg-success/15 text-success-text border-success/30' },
  agent_action:   { labelKey: 'chat.agentSteps.labels.action', badge: 'bg-accent/15 text-accent-text border-accent/30' },
  ux_snapshot:    { labelKey: 'chat.agentSteps.labels.ux', badge: 'bg-accent/15 text-accent-text border-accent/30' },
  agent_response: { labelKey: 'chat.agentSteps.labels.response', badge: NEUTRAL_BADGE },
  context_update: { labelKey: 'chat.agentSteps.labels.contextUpdate', badge: NEUTRAL_BADGE },
  // Card journey (api-service cards/trace.py): screen -> plates -> decision -> client ack.
  screen_facts:          { labelKey: 'chat.agentSteps.labels.screenFacts', badge: 'bg-slate-500/15 text-slate-300 border-slate-500/30' },
  card_plate:            { labelKey: 'chat.agentSteps.labels.cardPlate', badge: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  confirmation:          { labelKey: 'chat.agentSteps.labels.confirmation', badge: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  presentation_decision: { labelKey: 'chat.agentSteps.labels.decision', badge: 'bg-brand-bright/15 text-brand-bright border-brand-bright/30' },
  presentation_applied:  { labelKey: 'chat.agentSteps.labels.applied', badge: GOOD_BADGE },
  // A turn the system started (e.g. the screen failed to show what SAM sent) — not the user.
  system_event:          { labelKey: 'chat.agentSteps.labels.systemEvent', badge: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
};

/** Badge override that makes a problem jump out of the timeline without expanding it. */
function badgeFor(step: AgentRunStep): string | null {
  const m = step.metadata || {};
  if (step.step_type === 'card_plate') {
    const status = typeof m.status === 'string' ? m.status : '';
    if (status.startsWith('live')) return BAD_BADGE; // bypassed the turn-end decision
    if (status.startsWith('withheld') || status.startsWith('discarded')) return WARN_BADGE;
  }
  if (step.step_type === 'presentation_applied') {
    return m.result === 'applied' ? GOOD_BADGE : m.result === 'partial' ? WARN_BADGE : BAD_BADGE;
  }
  if (step.step_type === 'confirmation' && m.result !== 'approved') return WARN_BADGE;
  return null;
}

const asObj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' ? v as Record<string, unknown> : {});
const asStr = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : JSON.stringify(v));

function pretty(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') {
    try { return JSON.stringify(JSON.parse(v), null, 2); } catch { return v; }
  }
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

function fmtTime(ts?: string): string {
  if (!ts) return '';
  try { return new Date(ts).toLocaleTimeString(); } catch { return ''; }
}

// One-line preview per step type.
function previewOf(step: AgentRunStep): string {
  const c = step.content;
  const m = step.metadata || {};
  switch (step.step_type) {
    case 'user_input':
    case 'agent_response':
    case 'system_event':
      return asStr(c);
    case 'ux_snapshot':
      return asStr(m.caption) || asStr(c);
    case 'llm_call': {
      const obj = asObj(c);
      const tools = (asObj(obj.request_params).tools as unknown[]) || [];
      return `${asStr(obj.model) || 'model'} · ${tools.length} tool${tools.length === 1 ? '' : 's'}`;
    }
    case 'session_update': {
      const obj = asObj(c);
      const tools = (asObj(obj.request_params).tools as unknown[]) || [];
      const added = (m.tools_added as unknown[]) || [];
      const removed = (m.tools_removed as unknown[]) || [];
      const mods = (m.modules as unknown[]) || [];
      return [
        `${tools.length} tool${tools.length === 1 ? '' : 's'} (+${added.length} −${removed.length})`,
        mods.length ? `screen: ${mods.map(asStr).join(', ')}` : 'nothing open',
        m.ok === false ? 'FAILED — session kept its previous tools' : '',
      ].filter(Boolean).join(' · ');
    }
    case 'tool_call': {
      const obj = asObj(c);
      return `${asStr(obj.name) || 'tool'}(${asStr(obj.arguments)})`;
    }
    case 'tool_output': {
      const obj = asObj(c);
      return `${asStr(obj.name) || 'tool'} → ${asStr(obj.result)}`;
    }
    case 'agent_action':
      return [asStr(m.kind), asStr(m.module), asStr(m.caption) || asStr(m.title)].filter(Boolean).join(' · ');
    case 'screen_facts': {
      const where = asStr(m.card_definition_id) || ((m.modules as string[] | undefined) || []).join(', ');
      const age = typeof m.age_ms === 'number' ? `${m.age_ms} ms` : '—';
      return [asStr(m.surface), where, `${age} · ${asStr(m.source) || '—'}`].filter(Boolean).join(' · ');
    }
    case 'card_plate':
      return `#${asStr(m.call_seq)} ${asStr(m.tool)} → ${asStr(m.card) || asStr(m.module)} · ${asStr(m.kind)} · ${asStr(m.status)}`;
    case 'confirmation':
      return `${asStr(m.tool)} → ${asStr(m.result)} (${asStr(m.waited_ms)} ms)`;
    case 'presentation_decision':
      return `${asStr(m.decision)} · ${asStr(m.matched_rule)} · ↑${((m.emitted as unknown[]) || []).length} ✕${((m.dropped as unknown[]) || []).length}`;
    case 'presentation_applied':
      return [asStr(m.result), asStr(m.reason)].filter(Boolean).join(': ');
    default:
      return asStr(c);
  }
}

const Pre: React.FC<{ children: string }> = ({ children }) => {
  const { t } = useTranslation();
  return (
  <pre className="text-xs text-ui-text whitespace-pre-wrap font-mono bg-ui-subtle rounded border border-ui-border p-2 max-h-72 overflow-auto">
    {children || <span className="text-ui-text3 italic">{t('chat.agentSteps.empty')}</span>}
  </pre>
  );
};

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-micro font-medium uppercase tracking-wide text-ui-text2">{children}</p>
);

type RuleCheck = { rule_id?: string; result?: string; failed_on?: string };
type EntryRef = { call_seq?: number; tool?: string; card?: string; action_types?: string[]; reason?: string };

const RULE_STYLE: Record<string, string> = {
  matched: 'border-brand-bright/60 bg-brand-bright/10 text-brand-bright',
  no_match: 'border-ui-border text-ui-text2',
  not_evaluated: 'border-ui-border text-ui-text3 opacity-50',
  disabled: 'border-dashed border-ui-border text-ui-text3 opacity-50',
};

/** The HTML policy evaluator, replayed: every rule in order, the first match highlighted. */
const DecisionDetail: React.FC<{ m: Record<string, unknown> }> = ({ m }) => {
  const { t } = useTranslation();
  const rules = (m.rules_checked as RuleCheck[] | undefined) || [];
  const emitted = (m.emitted as EntryRef[] | undefined) || [];
  const dropped = (m.dropped as EntryRef[] | undefined) || [];
  const refLine = (e: EntryRef) =>
    `#${e.call_seq ?? '—'} ${e.tool ?? '—'} → ${e.card ?? (e.action_types || []).join(',')}${e.reason ? ` — ${e.reason}` : ''}`;
  const ruleLabel = (r: string | undefined) =>
    r === 'matched' ? t('chat.agentSteps.matched') : r === 'no_match' ? t('chat.agentSteps.noMatch')
      : r === 'disabled' ? t('chat.agentSteps.disabled') : t('chat.agentSteps.notEvaluated');
  return (
    <div className="space-y-2">
      <p className="text-[11px] text-ui-text2">
        <span className="font-mono text-brand-bright">{asStr(m.decision)}</span>
        <span className="ml-2">{t('chat.agentSteps.rule')}: <span className="font-mono">{asStr(m.matched_rule)}</span></span>
        {m.policy_version != null && <span className="ml-2 text-ui-text3">v{asStr(m.policy_version)}</span>}
      </p>
      {/* What the matched rule claims to do (its rulebook description). */}
      {asStr(m.rule_description) && <p className="text-[11px] italic text-ui-text2">{asStr(m.rule_description)}</p>}
      {rules.length > 0 && (
        <div className="space-y-1">
          <Label>{t('chat.agentSteps.rulesChecked')}</Label>
          {rules.map((r, i) => (
            <div key={i} className={`flex items-center gap-2 text-[11px] px-2 py-1 rounded border ${RULE_STYLE[r.result || 'not_evaluated'] || RULE_STYLE.not_evaluated}`}>
              <span className={`font-mono flex-1 min-w-0 truncate ${r.result === 'no_match' ? 'line-through decoration-ui-text3' : ''}`}>{r.rule_id}</span>
              <span className="flex-shrink-0">{ruleLabel(r.result)}</span>
              {r.failed_on && <span className="flex-shrink-0 font-mono text-ui-text3">{r.failed_on}</span>}
            </div>
          ))}
        </div>
      )}
      <Label>{t('chat.agentSteps.emitted')}</Label>
      <Pre>{emitted.length ? emitted.map(refLine).join('\n') : t('chat.agentSteps.none')}</Pre>
      <Label>{t('chat.agentSteps.dropped')}</Label>
      <Pre>{dropped.length ? dropped.map(refLine).join('\n') : t('chat.agentSteps.none')}</Pre>
      {m.facts != null && (<><Label>{t('chat.agentSteps.facts')}</Label><Pre>{pretty(m.facts)}</Pre></>)}
    </div>
  );
};

const MetaDetail: React.FC<{ m: Record<string, unknown> }> = ({ m }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <Label>{t('chat.agentSteps.metadata')}</Label>
      <Pre>{pretty(m)}</Pre>
    </div>
  );
};

const META_STEPS: StepType[] = ['screen_facts', 'card_plate', 'confirmation', 'presentation_applied', 'context_update'];

// Expanded detail per step type.
const StepDetail: React.FC<{ step: AgentRunStep }> = ({ step }) => {
  const { t } = useTranslation();
  const c = step.content;
  const m = step.metadata || {};
  if (step.step_type === 'llm_call' || step.step_type === 'session_update') {
    const obj = asObj(c);
    const isUpdate = step.step_type === 'session_update';
    const added = (m.tools_added as unknown[]) || [];
    const removed = (m.tools_removed as unknown[]) || [];
    const promptMsgs = (obj.prompt as { role?: string; content?: unknown }[]) || [];
    const tools = (asObj(obj.request_params).tools as unknown[]) || [];
    return (
      <div className="space-y-2">
        <p className="text-xs text-ui-text2">{t('chat.agentSteps.model')}: <span className="font-mono text-accent-text">{asStr(obj.model) || '—'}</span></p>
        {isUpdate && (
          <div className="space-y-1">
            {m.ok === false && <p className="text-xs text-danger-text">send failed: {asStr(m.error)} — the session kept its previous prompt and tools</p>}
            {added.length > 0 && (<><Label>Tools added ({added.length})</Label><Pre>{added.map(asStr).join('\n')}</Pre></>)}
            {removed.length > 0 && (<><Label>Tools removed ({removed.length})</Label><Pre>{removed.map(asStr).join('\n')}</Pre></>)}
            {added.length === 0 && removed.length === 0 && <p className="text-xs text-ui-text3">tool list unchanged (the prompt changed)</p>}
          </div>
        )}
        {promptMsgs.map((pm, i) => (
          <div key={i} className="space-y-1">
            <Label>{pm.role || t('chat.agentSteps.message')}</Label>
            <Pre>{asStr(pm.content)}</Pre>
          </div>
        ))}
        {tools.length > 0 && (<><Label>{t('chat.agentSteps.tools')} ({tools.length})</Label><Pre>{pretty(tools)}</Pre></>)}
      </div>
    );
  }
  if (step.step_type === 'tool_call') {
    const obj = asObj(c);
    const resultRaw = m.result ?? m.output ?? m.tool_result;
    const statusColor = asStr(m.status) === 'success' ? 'text-success-text'
      : asStr(m.status) === 'failed' ? 'text-danger-text' : 'text-ui-text3';
    return (
      <div className="space-y-1">
        <p className="text-xs text-ui-text2">
          {t('chat.agentSteps.tool')}: <span className="font-mono text-warning-text">{asStr(obj.name) || '—'}</span>
          {m.status ? <span className={`ml-2 ${statusColor}`}>{t('chat.agentSteps.status')}: {asStr(m.status)}</span> : null}
          {m.error ? <span className="ml-2 text-danger-text">{t('chat.agentSteps.error')}: {asStr(m.error)}</span> : null}
        </p>
        <Label>{t('chat.agentSteps.arguments')}</Label>
        <Pre>{pretty(obj.arguments)}</Pre>
        {resultRaw != null && (<><Label>{t('chat.agentSteps.result')}</Label><Pre>{pretty(resultRaw)}</Pre></>)}
      </div>
    );
  }
  if (step.step_type === 'tool_output') {
    const obj = asObj(c);
    const statusRaw = asStr(asObj(obj.result).status || asObj(c).status || '');
    const statusColor = statusRaw === 'success' ? 'text-success-text'
      : statusRaw === 'failed' ? 'text-danger-text' : '';
    return (
      <div className="space-y-1">
        <p className="text-xs text-ui-text2">
          {t('chat.agentSteps.tool')}: <span className="font-mono text-success-text">{asStr(obj.name) || '—'}</span>
          {statusRaw && <span className={`ml-2 ${statusColor}`}>{t('chat.agentSteps.status')}: {statusRaw}</span>}
        </p>
        <Label>{t('chat.agentSteps.result')}</Label>
        <Pre>{pretty(obj.result)}</Pre>
      </div>
    );
  }
  if (step.step_type === 'agent_action') {
    return (
      <div className="space-y-1">
        <Label>{t('chat.agentSteps.actionMetadata')}</Label>
        <Pre>{pretty(m)}</Pre>
      </div>
    );
  }
  if (step.step_type === 'presentation_decision') return <DecisionDetail m={m} />;
  if (META_STEPS.includes(step.step_type)) return <MetaDetail m={m} />;
  // text-y steps
  return <Pre>{asStr(c)}</Pre>;
};

const StepRow: React.FC<{ step: AgentRunStep; index: number }> = ({ step, index }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const base = STEP_META[step.step_type] ?? { label: step.step_type, badge: NEUTRAL_BADGE };
  const meta = { label: base.labelKey ? t(base.labelKey) : base.label, badge: badgeFor(step) ?? base.badge };
  return (
    <div className="border border-ui-border rounded-lg bg-ui-subtle overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-ui-hover transition">
        <span className="text-xs font-mono text-ui-text3 w-5 flex-shrink-0 text-right">{index}</span>
        <span className={`flex-shrink-0 text-xs font-medium px-1.5 py-0.5 rounded border ${meta.badge}`}>{meta.label}</span>
        <span className="flex-1 min-w-0 text-xs text-ui-text2 truncate">{previewOf(step)}</span>
        <span className="flex-shrink-0 text-xs text-ui-text3 font-mono">{fmtTime(step.timestamp)}</span>
        <span className={`flex-shrink-0 text-ui-text3 transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
      </button>
      {open && <div className="px-2.5 pb-2.5 pt-1 border-t border-ui-border"><StepDetail step={step} /></div>}
    </div>
  );
};

export const AgentStepsView: React.FC<{
  steps: AgentRunStep[];
  targetIndex?: number;
  targetRef?: React.RefObject<HTMLDivElement | null>;
}> = ({ steps, targetIndex, targetRef }) => {
  const { t } = useTranslation();
  if (!steps.length) return <p className="text-xs text-ui-text2 py-2">{t('chat.agentSteps.noSteps')}</p>;
  return (
    <div className="space-y-1">
      {steps.map((s, i) => (
        <div
          key={i}
          ref={i === targetIndex ? targetRef : undefined}
          className={i === targetIndex ? 'ring-1 ring-ui-ring/60 rounded-lg' : ''}
        >
          <StepRow step={s} index={i + 1} />
        </div>
      ))}
    </div>
  );
};

// Inline per-turn container: a "View agent steps" toggle that lazily fetches the turn's run trace.
export const TurnSteps: React.FC<{ conversationId: string; turnId: string }> = ({ conversationId, turnId }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data, isFetching, isError } = useGetConversationRunsQuery(
    { conversationId, turnId },
    { skip: !open || !conversationId || !turnId },
  );
  const steps = ((data as AgentRun[] | undefined) || []).flatMap(r => r.steps || []);

  return (
    <div className="mt-1 ml-1">
      <button
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1 text-xs text-ui-text3 hover:text-ui-text2 transition"
      >
        <span className={`transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
        {open ? t('chat.agentSteps.hide') : t('chat.agentSteps.view')}
      </button>
      {open && (
        <div className="mt-1.5 max-w-[85%]">
          {isFetching && <p className="text-xs text-ui-text2 py-1">{t('chat.agentSteps.loading')}</p>}
          {isError && <p className="text-xs text-danger-text py-1">{t('chat.agentSteps.loadFailed')}</p>}
          {!isFetching && !isError && <AgentStepsView steps={steps} />}
        </div>
      )}
    </div>
  );
};

export default AgentStepsView;
